import { useState } from 'react'

import { Box, Text, theme } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Cross } from '@factorialco/f0-react/icons/app'
import { InAppCommunicationQuery, Maybe } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import CommsButton from 'modules/in_app_comms/components/CommsButton'

type BannerContent =
  InAppCommunicationQuery['inAppComms']['communicationsConnection']['nodes'][number]['content']['banner']

const CustomAvatarEmoji = ({ emoji }: { emoji: string }) => {
  return (
    <Box
      borderRadius={{ all: 'abs012' }}
      style={{
        background: 'rgba(255, 255, 255, 0.15)',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
      }}
    >
      {emoji}
    </Box>
  )
}

interface BannerProps {
  content: Maybe<BannerContent> | undefined
  componentName: string
  onDismiss: () => void
}

const Banner = ({ content, componentName, onDismiss }: BannerProps) => {
  const handleClose = () => {
    onDismiss()
    track(trackingEvents.inAppComms.dismissInAppCommsComponent, {
      component: 'in_app_comms_banner',
      componentName,
    })
  }

  useEffect(() => {
    if (!content?.title) return
    track(trackingEvents.inAppComms.viewInAppCommsComponent, {
      component: 'in_app_comms_banner',
      componentName,
    })
  }, [content?.title, componentName])

  if (!content) return null

  const colorsTheme = () => {
    switch (content?.backgroundColor) {
      case 'one_gradient':
        return {
          background:
            'linear-gradient(98.39deg, rgba(249, 115, 22, 0.7) 0%, rgba(229, 25, 67, 0.7) 20%, rgba(229, 25, 67, 0.7) 49.97%, rgba(229, 25, 67, 0.7) 80%, rgba(164, 165, 222, 0.7) 100%)',
          buttonVariant: 'outline',
        }
      case 'black_friday_gradient':
        return {
          background: 'linear-gradient(120deg, #0c0c14 0%, #2e3a45 40%, #5a3a44 70%, #793341 100%)',
          buttonVariant: 'default',
        }
      // TODO: Remove once is not used in DatoCMS anymore
      case 'radical':
        return {
          background: '#FF355E',
          buttonVariant: 'outline',
        }
      case 'black':
        return {
          background: 'black',
          buttonVariant: 'default',
        }
      // TODO: Remove once is not used in DatoCMS anymore
      case 'viridian':
        return {
          background: '#06838B',
          buttonVariant: 'outline',
        }
      default:
        return {
          background: 'grey000White',
          buttonVariant: 'default',
        }
    }
  }

  const Countdown = () => {
    const [timeLeft, setTimeLeft] = useState({
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    })

    useEffect(() => {
      const calculateTimeLeft = () => {
        const now = new Date().getTime()
        const target = new Date(content.countdown || '').getTime()
        const difference = target - now

        if (difference > 0) {
          setTimeLeft({
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((difference % (1000 * 60)) / 1000),
          })
        } else {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        }
      }

      calculateTimeLeft()
      const timer = setInterval(calculateTimeLeft, 1000)

      return () => clearInterval(timer)
    }, [])

    return (
      <Box flexDirection="row" alignItems="center" gap="s4">
        <Text.Normal
          size="200"
          weight="semibold"
          color={content.backgroundColor ? 'white' : 'grey1000'}
        >
          {timeLeft.days}d
        </Text.Normal>
        <Text.Normal
          size="200"
          weight="semibold"
          color={content.backgroundColor ? 'white' : 'grey1000'}
        >
          {timeLeft.hours.toString().padStart(2, '0')}h
        </Text.Normal>
        <Text.Normal
          size="200"
          weight="semibold"
          color={content.backgroundColor ? 'white' : 'grey1000'}
        >
          {timeLeft.minutes.toString().padStart(2, '0')}m
        </Text.Normal>
        <Text.Normal
          size="200"
          weight="semibold"
          color={content.backgroundColor ? 'white' : 'grey1000'}
        >
          {timeLeft.seconds.toString().padStart(2, '0')}s
        </Text.Normal>
      </Box>
    )
  }

  return (
    <div onPointerDown={(e) => e.stopPropagation()}>
      <Box
        flexDirection="row"
        position="relative"
        width="full"
        padding="s6"
        gap="s6"
        style={{
          marginBottom: theme.dimensions.s8,
          borderBottom: content.backgroundColor ? '' : '1px dashed',
          borderColor: theme.colors.grey300,
          zIndex: theme.zIndex.banner,
          left: '0',
          right: '0',
          top: '0',
          background: colorsTheme().background,
        }}
      >
        <Box flexDirection="row" alignItems="center" justifyContent="center" width="full" gap="s32">
          <Box flexDirection="row" alignItems="center" gap="s8">
            {content.icon && <CustomAvatarEmoji emoji={content.icon} />}
            {content.countdown && (
              <Text.Normal size="200" color="white">
                <Countdown />
              </Text.Normal>
            )}
            <Box flexDirection="row" alignItems="center" gap="s8" flexWrap="wrap">
              {content.title && (
                <Text.Normal
                  size="200"
                  weight="semibold"
                  color={content.backgroundColor ? 'white' : 'grey1000'}
                >
                  {content.title}
                </Text.Normal>
              )}
              {content.description && (
                <Text.Normal size="200" color={content.backgroundColor ? 'white' : 'grey800'}>
                  {content.description}
                </Text.Normal>
              )}
            </Box>
          </Box>
          <Box flexDirection="row" gap="s8">
            {content.buttons.map((button, index) => {
              return (
                <CommsButton
                  key={index}
                  button={button}
                  variant={colorsTheme().buttonVariant}
                  size={content.icon ? 'md' : 'sm'}
                  componentType="in_app_comms_banner"
                  componentName={componentName}
                  onClick={() => onDismiss()}
                  upsellRequestComponentType="in_app_comms_banner"
                />
              )
            })}
          </Box>
        </Box>
        <Box alignSelf="flexStart" paddingRight="s16">
          <F0Button
            onClick={handleClose}
            variant="ghost"
            size="md"
            icon={Cross}
            hideLabel
            round
            label="Ghost"
          />
        </Box>
      </Box>
    </div>
  )
}

export default Banner
